import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import { Typography } from '@deeptrue-frontend/ui';
import { translateGroupName } from '../utils';
import Legend from './Legend';
import {
  ReportType,
  SubgroupType,
  LocaleType,
  ChartWrapperType,
} from '../types';
import Charts from 'components/Charts';

const Content = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  margin-left: 2rem;

  & > div:first-of-type {
    position: relative;
    margin-bottom: 4rem;
    border-right: 1px solid ${({ theme }) => theme.colors.quillGray};
    width: 30rem;
    margin-left: -2rem;
    background-color: white;
  }
`;
const ReportContainer = styled.div`
  width: 100%;
  padding: 1rem 2rem;
`;

const Segment = styled.div`
  padding: 0 0 2.5rem;
  border-bottom: 1px solid #e6e6e5;
  padding: 2.5rem 0 2.5rem;

  &:first-of-type {
    padding: 0 0 2.5rem;
  }
`;

const SubSegment = styled.div`
  margin-top: 1rem;
`;

const SegmentTitle = styled(Typography)`
  color: #454a54;
  text-transform: uppercase;
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 0.875rem;
`;

const SubSegmentTitle = styled(Typography)`
  color: #454a54;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

interface Props {
  setSection: (sectionName: string) => void;
  selectedSection?: string;
  dataToDisplay: ReportType[];
}

const defineChartType = (chartData: any, locale: LocaleType) => {
  if (!chartData) return null;

  const { type, data } = chartData;

  switch (type) {
    case 'bar-stacked':
    case 'bar-stacked-combined':
      return <Charts.StackedBar data={data} locale={locale} />;
    case 'open-question':
    case 'open-question-stacked-combined':
      return <Charts.OpenQuestion data={data} locale={locale} />;
    case 'bar':
      return <Charts.Bar data={data} locale={locale} />;
    case 'bar-nps':
      return <Charts.Bar data={data} locale={locale} nps={chartData.nps} />;
    case 'bar-detailed':
      return <Charts.Bar data={data} locale={locale} isDetailed />;
    case 'activation':
      return <Charts.Activation chartData={chartData} locale={locale} />;
    case 'statements-bar':
      return (
        <Charts.Statement
          data={data}
          locale={locale}
          customZeroAnswersLabel={chartData.customZeroAnswersLabel}
        />
      );
    case 'statements-bar-raw':
      return <Charts.Statement data={data} locale={locale} showZeroBars />;
    default:
      return;
  }
};

const Results = ({ setSection, selectedSection, dataToDisplay }: Props) => {
  const { locale } = useIntl();

  useEffect(() => {
    if (selectedSection) {
      const elemToScroll = document.getElementById(selectedSection);
      const firstElement =
        typeof dataToDisplay[0].group_name === 'string'
          ? dataToDisplay[0].group_name
          : //@ts-ignore
            dataToDisplay[0].group_name[locale];

      if (elemToScroll) {
        // HACK to do not scroll if 1st element is shown
        const isFirst =
          elemToScroll.id === firstElement && window.pageYOffset === 0;

        const y =
          elemToScroll.getBoundingClientRect().top + window.pageYOffset + -85;

        !isFirst && window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [selectedSection, dataToDisplay, locale]);

  return (
    <Content>
      <Legend
        setSection={setSection}
        selectedSection={selectedSection}
        reportsLegend={[...dataToDisplay]}
      />
      <ReportContainer>
        {dataToDisplay.map((reportSegment: ReportType, index: number) => {
          const { subgroups, group_name, charts } = reportSegment;

          const groupTitle = translateGroupName(group_name, locale);

          return (
            <Segment key={`${groupTitle}-${index}`} id={groupTitle}>
              <SegmentTitle>{groupTitle}</SegmentTitle>
              {subgroups
                ? subgroups.map((subgroup: SubgroupType, index: number) => {
                    const { name, charts } = subgroup;
                    const subgroupTitle = translateGroupName(name, locale);

                    return (
                      <SubSegment key={`${subgroupTitle}-${index}`}>
                        <SubSegmentTitle id={`${groupTitle}-${subgroupTitle}`}>
                          {subgroupTitle}
                        </SubSegmentTitle>
                        {charts &&
                          charts.map(
                            (chartData: ChartWrapperType, index: number) => {
                              const {
                                title,
                                isCollapsable,
                                mainChart,
                                secondaryChart,
                              } = chartData;

                              // INFO Special case for Displaying Open question charts
                              const isTitleHidden =
                                mainChart.type === 'open-question' &&
                                (!secondaryChart ||
                                  secondaryChart.type === 'open-question');
                              const subtitle =
                                mainChart.type === 'activation'
                                  ? 'subtitle'
                                  : null;

                              // INFO Special case for
                              const isTitleChanged =
                                mainChart.type === 'bar-stacked-combined' &&
                                mainChart.overridenOtherText;

                              return (
                                <Charts.ChartWrapper
                                  key={index}
                                  title={
                                    isTitleHidden
                                      ? ''
                                      : isTitleChanged
                                      ? mainChart.overridenOtherText
                                      : title
                                  }
                                  subtitle={subtitle}
                                  keyMessage={chartData.key_message}
                                  locale={locale as LocaleType}
                                  isCollapsable={!!isCollapsable}
                                  mainChart={defineChartType(
                                    mainChart,
                                    locale as LocaleType
                                  )}
                                  secondaryChart={defineChartType(
                                    secondaryChart,
                                    locale as LocaleType
                                  )}
                                />
                              );
                            }
                          )}
                      </SubSegment>
                    );
                  })
                : charts &&
                  charts.map((chartData: ChartWrapperType, index: number) => {
                    const { title, isCollapsable, mainChart, secondaryChart } =
                      chartData;

                    // INFO Special case for Displaying Open question charts
                    const isTitleHidden =
                      mainChart.type === 'open-question' &&
                      (!secondaryChart ||
                        secondaryChart.type === 'open-question');

                    return (
                      <Charts.ChartWrapper
                        key={index}
                        title={isTitleHidden ? '' : title}
                        locale={locale as LocaleType}
                        isCollapsable={!!isCollapsable}
                        mainChart={defineChartType(
                          mainChart,
                          locale as LocaleType
                        )}
                        secondaryChart={defineChartType(
                          secondaryChart,
                          locale as LocaleType
                        )}
                      />
                    );
                  })}
            </Segment>
          );
        })}
      </ReportContainer>
    </Content>
  );
};

export default Results;
